import { useEffect, useState } from "react";
import { Instagram, RefreshCw, Save, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { getInstagramConfig, saveInstagramConfig, syncInstagram } from "@/lib/api";

const inputCls =
  "w-full bg-[#0D0D10] border border-white/15 text-white text-sm px-3 py-2.5 focus:border-[#FFD400] focus:outline-none transition-colors placeholder:text-slate-600";

const STEPS = [
  "Convert @khabriknews to a Business/Creator account (Instagram app → Settings → Account type)",
  "Create a Facebook Page and link it to the Instagram account (Page Settings → Linked Accounts → Instagram)",
  "Go to developers.facebook.com/apps → Create App → add 'Facebook Login for Business'",
  "Open Graph API Explorer, select your app, grant permissions: instagram_basic, pages_show_list, pages_read_engagement, then Generate Access Token",
  "Get your IG User ID: call me/accounts to get PAGE_ID, then {PAGE_ID}?fields=instagram_business_account — copy the numeric id",
  "Paste both below and click Save, then Sync Now",
];

export default function AdminInstagram({ onSynced }) {
  const [token, setToken] = useState("");
  const [igId, setIgId] = useState("");
  const [configured, setConfigured] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    getInstagramConfig()
      .then((c) => {
        setConfigured(c.configured);
        setIgId(c.ig_user_id || "");
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveInstagramConfig(token, igId);
      setConfigured(true);
      setToken("");
      toast.success("Instagram credentials saved");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await syncInstagram();
      toast.success(`Imported ${res.imported} posts as articles (${res.skipped} skipped)`);
      onSynced?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="admin-instagram">
      <div className="border border-white/10 p-6">
        <h3 className="font-editorial font-bold text-white text-lg flex items-center gap-2">
          <Instagram size={16} className="text-[#FFD400]" /> Instagram Sync — @khabriknews
        </h3>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          Imports every post's caption + image from your Instagram page as an article. First caption line becomes the title.
        </p>
        {configured && (
          <p className="mt-4 flex items-center gap-2 text-sm text-[#30D158]" data-testid="instagram-configured-badge">
            <CheckCircle2 size={14} /> Credentials saved{igId ? ` (IG ID: ${igId})` : ""}
          </p>
        )}
        <form onSubmit={save} className="mt-5 space-y-4">
          <label className="block">
            <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">Meta Access Token</span>
            <input data-testid="instagram-token-input" className={inputCls} value={token} onChange={(e) => setToken(e.target.value)} placeholder={configured ? "•••••• (saved — paste to replace)" : "EAAG..."} />
          </label>
          <label className="block">
            <span className="font-mono-meta text-[10px] text-slate-400 uppercase tracking-widest block mb-1.5">Instagram User ID (numeric)</span>
            <input data-testid="instagram-igid-input" className={inputCls} value={igId} onChange={(e) => setIgId(e.target.value)} placeholder="1784xxxxxxxxxxx" />
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="instagram-save-button" className="flex items-center gap-2 border border-white/15 text-slate-300 px-5 py-2.5 text-sm font-semibold hover:text-[#FFD400] hover:border-[#FFD400] transition-colors disabled:opacity-50">
              <Save size={14} /> {saving ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={sync} disabled={syncing || !configured} data-testid="instagram-sync-button" className="flex items-center gap-2 bg-[#FFD400] text-[#0D0D10] px-5 py-2.5 text-sm font-bold hover:bg-[#E6BE00] transition-colors disabled:opacity-50">
              <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Now"}
            </button>
          </div>
        </form>
      </div>
      <div className="border border-white/10 p-6">
        <h3 className="font-editorial font-bold text-white text-lg">Setup Steps</h3>
        <ol className="mt-4 space-y-3">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
              <span className="font-display text-[#FFD400] flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
              {s}
            </li>
          ))}
        </ol>
        <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#FFD400] hover:underline">
          Open Meta for Developers <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
